// SVG line chart of hourly-average temperatures. One line per equipment.
// The target band (green) shades the area between temp_low_f and
// temp_high_f for whichever equipment has a band; readings outside it
// jump out visually.
export function HourlyTempChart({ hourlyAverages }) {
  if (!hourlyAverages || hourlyAverages.length === 0) {
    return <div style={{ fontSize: 12, color: '#888', padding: 12 }}>No readings in the selected range.</div>;
  }
  // Group by equipment so we can draw one polyline per piece.
  const byEq = new Map();
  for (const h of hourlyAverages) {
    if (!byEq.has(h.equipment_id)) byEq.set(h.equipment_id, {
      id: h.equipment_id, name: h.equipment_name,
      low: h.temp_low_f, high: h.temp_high_f, points: [],
    });
    byEq.get(h.equipment_id).points.push({ t: new Date(h.hour).getTime(), v: h.avg_temperature });
  }
  for (const eq of byEq.values()) eq.points.sort((a, b) => a.t - b.t);
  const allPoints = hourlyAverages.map(h => ({ t: new Date(h.hour).getTime(), v: h.avg_temperature }));
  const minT = Math.min(...allPoints.map(p => p.t));
  const maxT = Math.max(...allPoints.map(p => p.t));
  const minV = Math.min(...allPoints.map(p => p.v)) - 2;
  const maxV = Math.max(...allPoints.map(p => p.v)) + 2;
  const W = 720, H = 260, PL = 36, PR = 12, PT = 12, PB = 36;
  const innerW = W - PL - PR, innerH = H - PT - PB;
  const xFor = (t) => PL + ((t - minT) / Math.max(1, maxT - minT)) * innerW;
  const yFor = (v) => PT + innerH - ((v - minV) / Math.max(0.001, maxV - minV)) * innerH;
  const colors = ['#1565c0', '#2e7d32', '#c62828', '#6a1b9a', '#d97706', '#0f766e', '#9d174d'];

  // Y-axis ticks (every 5°F or so).
  const tickStep = (maxV - minV) > 30 ? 10 : 5;
  const yTicks = [];
  for (let v = Math.ceil(minV / tickStep) * tickStep; v <= maxV; v += tickStep) yTicks.push(v);

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 260, display: 'block', background: '#fff', border: '1px solid #f0f0f0', borderRadius: 6 }}>
        {/* Target band — shaded green between first equipment's low/high.
            (If multiple equipment with different targets, just show the
            first; the CSV has full per-row in_range data.) */}
        {Array.from(byEq.values())[0]?.low != null && Array.from(byEq.values())[0]?.high != null && (
          <rect x={PL} y={yFor(Array.from(byEq.values())[0].high)} width={innerW}
                height={Math.max(0, yFor(Array.from(byEq.values())[0].low) - yFor(Array.from(byEq.values())[0].high))}
                fill="#dcfce7" opacity="0.5" />
        )}
        {yTicks.map((v, i) => (
          <g key={i}>
            <line x1={PL} y1={yFor(v)} x2={W - PR} y2={yFor(v)} stroke="#eee" strokeWidth="1" />
            <text x={PL - 4} y={yFor(v) + 3} fontSize="10" fill="#9e9e9e" textAnchor="end">{v}°</text>
          </g>
        ))}
        {Array.from(byEq.values()).map((eq, i) => {
          const color = colors[i % colors.length];
          const pts = eq.points.map(p => `${xFor(p.t)},${yFor(p.v)}`).join(' ');
          return (
            <g key={eq.id}>
              <polyline points={pts} fill="none" stroke={color} strokeWidth="2" />
              {eq.points.map((p, j) => (
                <circle key={j} cx={xFor(p.t)} cy={yFor(p.v)} r="2" fill={color}>
                  <title>{`${eq.name}: ${p.v}°F at ${new Date(p.t).toLocaleString()}`}</title>
                </circle>
              ))}
            </g>
          );
        })}
        {/* X-axis: start + middle + end timestamps */}
        {[minT, (minT + maxT) / 2, maxT].map((t, i) => (
          <text key={i} x={xFor(t)} y={H - 10} fontSize="10" fill="#9e9e9e"
                textAnchor={i === 0 ? 'start' : i === 2 ? 'end' : 'middle'}>
            {new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </text>
        ))}
      </svg>
      <div style={{ display: 'flex', gap: 12, marginTop: 4, fontSize: 11, color: '#555', flexWrap: 'wrap' }}>
        {Array.from(byEq.values()).map((eq, i) => (
          <span key={eq.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 12, height: 3, background: colors[i % colors.length], display: 'inline-block' }} />
            {eq.name}{eq.low != null && eq.high != null ? ` (${eq.low}–${eq.high}°F)` : ''}
          </span>
        ))}
      </div>
    </div>
  );
}
